import {
    observer
} from 'mobx-react-lite';
import type { ApplicationSdk } from '../../stores';
import { useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { formatDate } from '../../helper';
import { Toolbar } from 'primereact/toolbar';
import CleanerManagerStore from '../../stores/manager/CleanerManagerStore';
import CleanerDetailStore from '../../stores/manager/dialog/CleanerDialogStore';
import SearchField from '../../components/SearchField';
import { Paginator } from 'primereact/paginator';
import { Chip } from 'primereact/chip';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dropdown } from 'primereact/dropdown';

const CleanerManager = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            label="Neu"
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(CleanerDetailStore, {})}
            style={{ marginRight: '0.5rem' }}
        />
    </>
});

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedManager = sdk.currentStore?.filterManager;
    const selectedTraining = sdk.currentStore?.filterTrainingLabel;
    // const selectedCategories = sdk.currentStore?.filterCategories || [];
    const selectedSearch = sdk.currentStore?.searchValue || '';
    const clearManagerFilter = () => {
        sdk.currentStore.applyFilterManager(null); // Clear the manager filter
    };
    const clearTrainingFilter = () => {
        sdk.currentStore.applyFilterTraining(null); // Clear the manager filter
        sdk.currentStore.applyFilterTrainingStatus(null); // Clear the manager filter
    };

    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedManager && (
                <Chip
                    label={`Objektleiter: ${selectedManager.name}`}
                    removable
                    onRemove={clearManagerFilter}
                />
            )}
            {selectedTraining && (
                <Chip
                    label={`${selectedTraining}`}
                    removable
                    onRemove={clearTrainingFilter}
                />
            )}
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});



const FilterPopover = observer(({ sdk }: { sdk: ApplicationSdk }) => {

    // const [selectedManager, setSelectedManager] = useState(null);

    const loadManagers = async () => {
        if (sdk.currentStore) {
            sdk.currentStore.fetchAvailableManagers();
        }
    };

    const onManagerChange = (e: any) => {
        sdk.currentStore.applyFilterManager(e.value);
    };

    const onTrainingChange = (e: any) => {
        sdk.currentStore.applyFilterTraining(e.value);
    };
    const loadTrainings = async () => {
        if (sdk.currentStore) {
            sdk.currentStore.fetchAvailableTrainings();
        }
    };
    const onStatusChange = (e: any) => {
        console.log(e.value);
        sdk.currentStore.applyFilterTrainingStatus(e.value); // Add logic to handle status filter
    };

    return (
        <div>
            <h4 style={{ marginBottom: '1rem' }}>Objektleiter auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterManager || null}
                options={sdk.currentStore?.available_managers || []}
                onChange={onManagerChange}
                onShow={loadManagers} // Lazy load managers when dropdown is focused
                placeholder="Objektleiter auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%' }}
            />
            <h4 style={{ marginBottom: '1rem' }}>Schulung auswählen</h4>
            <Dropdown
                value={sdk.currentStore?.filterTraining || null}
                options={sdk.currentStore?.available_trainings || []}
                onChange={onTrainingChange}
                onShow={loadTrainings}
                placeholder="Schulung auswählen"
                optionLabel="name"
                loading={false}
                style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <Dropdown
                value={sdk.currentStore?.filterTrainingStatus || 0}
                options={[
                    { label: 'Bestanden', value: 1 },
                    { label: 'Nicht bestanden', value: 0 },
                ]}
                onChange={onStatusChange}
                placeholder="Status auswählen"
                style={{ width: '100%' }}
            />
        </div>
    );
});

const SearchToolbar = ({ sdk }: { sdk: ApplicationSdk }) => {
    const ref: any = useRef(null);

    return <>
        <Toolbar
            start={() => <CleanerManager sdk={sdk} />}
            center={() => (
                <>
                    <Button
                        icon="pi pi-filter"
                        className="p-button-text"
                        label="Filter"
                        onClick={(e: any) => ref.current?.toggle(e)}
                        aria-label="Filter"
                    />
                    <OverlayPanel ref={ref} style={{ width: '300px' }}>
                        <FilterPopover sdk={sdk} />
                    </OverlayPanel>
                </>
            )}
            end={() => <SearchField store={sdk.currentStore} placeholder={'Reinigungskräfte durchsuchen'} />}
        />
        <FilterChips sdk={sdk} />
    </>
}

const CleanerOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(CleanerManagerStore);
    }, [sdk]);

    const cleaners = sdk.currentStore?.allCleaners || null;

    const onPageChange = (event: any) => {
        sdk.currentStore.onPageChange(event.page + 1);
    };

    const rowClassName = (data: any) => {
        return {
            'red-row': false // later if too less trainings
        };
    };

    return <div className="card">
        <DataTable
            value={cleaners}
            emptyMessage={'Keine Reinigungskräfte gefunden'}
            header={<SearchToolbar sdk={sdk} />}
            onSort={(event: any) => {
                sdk.currentStore.onSort(event.sortField);
                return event;
            }}
            sortOrder={sdk.currentStore?.sortOrder || 1}
            sortField={sdk.currentStore?.sortField || 'lastname'}
            rowHover={true}
            loading={!!sdk.currentStore?.cleanerListLoading}
            rowClassName={rowClassName}
        >
            {/* <Column field="pri" header="Status"></Column> PRICING? Free or paid*/}
            <Column field="id" header="Id" sortable></Column>
            <Column field="firstname" header="Vorname" sortable></Column>
            <Column field="lastname" header="Nachname" sortable></Column>
            <Column field="phonenumber" header="Rufnummer" sortable></Column>
            <Column field="solved_training" header="Schulungen" sortable></Column>
            <Column field={'created'} header="Erstellt" sortable body={(p: any) => formatDate(p.created)}></Column>
            <Column field={'updated'} header="Aktualisiert" sortable body={(p: any) => formatDate(p.updated)}></Column>
            <Column header="" body={(c: any) => <Button icon="pi pi-pencil" rounded text aria-label="Filter" onClick={() => sdk.openDialog(CleanerDetailStore, { cleaner_id: c.id })} />}></Column>
        </DataTable>
        <Paginator
            first={((sdk.currentStore?.currentPage || 1) - 1) * 30}
            rows={30}
            totalRecords={sdk.currentStore?.totalCount || 1}
            onPageChange={onPageChange}
            // rowsPerPageOptions={30}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate={`Seite {currentPage} / {totalPages} (${sdk.currentStore?.totalCount || 0} Reinigungskräfte)`}
        />
    </div>;
});

export default CleanerOverview;